// src/components/Login.js
// Sacred Canvas login — fully fluid layout + demo account quick-login.

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isParentRole, isStudentRole, userService, ROLES } from '../services/userService';
import { studentService } from '../services/firebaseService';
import { adminSeedService } from '../services/adminSeedService';
import {
  Box, Button, Chip, CircularProgress, Collapse, IconButton, Link, Stack, Tab, Tabs, TextField, Typography, Alert, Divider,
} from '@mui/material';
import { ArrowForward, ExpandMore, Science } from '@mui/icons-material';
import { MaghribiHeading, ZelligeBackdrop, ArabicMotif } from './sacred';

const DEMO_ACCOUNTS = [
  {
    label: 'المدير',
    email: 'jamal.mazraoui@director.albayan.com',
    password: 'Admin123!',
    role: 'admin',
    displayName: 'ذ. جمال المزراوي',
    description: 'لوحة القيادة الكاملة',
  },
  {
    label: 'معلمة',
    email: 'hafsa.merouani@teacher.albayan.com',
    password: 'Teacher123!',
    role: 'teacher',
    displayName: 'ذ. حفصة المرواني',
    description: 'حلقة أبي بكر الصديق',
  },
  {
    label: 'ولي أمر',
    email: 'ibrahim.khattabi@parent.albayan.com',
    password: 'Parent123!',
    role: 'parent',
    displayName: 'إبراهيم الخطابي',
    description: 'ولي أمر طالبتين (هدى وخلود)',
  },
];

const Login = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const { login, loginWithGoogle, startPhoneSignIn, confirmPhoneSignIn, refreshProfile, resetPassword } = useAuth();
  const navigate = useNavigate();

  const redirectAfterLogin = async (user) => {
    let profile = null;
    try { if (user?.uid) profile = await userService.getUserProfile(user.uid); } catch (_) {}
    try { if (typeof refreshProfile === 'function') await refreshProfile(); } catch (_) {}
    const role = profile?.role;
    if (isStudentRole(role)) navigate('/student/home');
    else if (isParentRole(role)) navigate('/parent/dashboard');
    else navigate('/dashboard');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { const cred = await login(email, password); await redirectAfterLogin(cred?.user); }
    catch (err) {
      // Phase 1.14: users imported from Firebase land with passwordHash=null
      // and mustResetPassword=true. The API backend returns 403 with
      // `mustResetPassword: true`; auto-trigger the reset email and tell
      // them to check their inbox so first-login is unblocked.
      if (err?.body?.mustResetPassword || err?.status === 403) {
        try {
          if (typeof resetPassword === 'function') await resetPassword(email);
          setError('تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك. تحقق من البريد لإكمال أول تسجيل دخول.');
          console.warn('[login] forced password reset for', email);
          return;
        } catch (resetErr) {
          console.error('[login] reset trigger failed', resetErr);
        }
      }
      setError(t('auth.loginFailed'));
      console.error(err);
    }
    finally { setLoading(false); }
  };

  const loginAsDemo = async (acct) => {
    setError(''); setLoading(true);
    setEmail(acct.email); setPassword(acct.password); setTab(0);
    try {
      let cred;
      try {
        cred = await login(acct.email, acct.password);
      } catch (firstErr) {
        // The demo admin row may be missing entirely (fresh DB) or have
        // passwordHash=null (Firebase-imported, never reset). Both surface
        // as 401/403 from /api/auth/login. Hit the public bootstrap
        // endpoint to (re)create the canonical demo admin with a fresh
        // bcrypt hash, then retry once. Disabled in production unless
        // DEMO_ENABLED=true on the backend, in which case bootstrap 404s
        // and the original error is re-thrown.
        const status = firstErr?.status;
        if (status !== 401 && status !== 403) throw firstErr;
        try {
          await adminSeedService.bootstrapDemoAdmin();
        } catch (bootErr) {
          console.error('[demo-login] bootstrap failed', bootErr);
          throw firstErr;
        }
        cred = await login(acct.email, acct.password);
      }
      await redirectAfterLogin(cred?.user);
    } catch (err) {
      console.error('[demo-login]', err);
      setError(`تعذر تسجيل الدخول التجريبي (${err?.message || 'خطأ غير معروف'}).`);
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = async () => {
    setError(''); setLoading(true);
    try {
      const cred = await loginWithGoogle();
      await redirectAfterLogin(cred?.user);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/popup-closed-by-user') return; // user cancelled
      setError('تعذر تسجيل الدخول عبر جوجل. حاول مرة أخرى.');
    } finally { setLoading(false); }
  };

  const handleSendOtp = async () => {
    setError('');
    if (!phone) { setError(t('auth.phoneRequired')); return; }
    // Firebase requires the E.164 format: + then country code then number.
    const trimmed = phone.trim().replace(/\s+/g, '');
    if (!/^\+[1-9]\d{6,14}$/.test(trimmed)) {
      setError(t('auth.otpBadNumber'));
      return;
    }
    setLoading(true);
    try {
      const r = await startPhoneSignIn(trimmed, 'recaptcha-container');
      setConfirmation(r);
    } catch (err) {
      console.error('[phone-auth]', err);
      // Map the specific Firebase error to an actionable message so the
      // admin can fix the config instead of retrying the same call.
      const code = err?.code || '';
      // `error-code:-39` is the catch-all surfaced by Firebase when the
      // backend rejected the request at the reCAPTCHA/App-Check layer.
      // Treat it as the reCAPTCHA-failed case so the admin sees an
      // actionable message.
      const mapped = (() => {
        if (/-39\b/.test(code) || code === 'auth/error-code:-39') {
          return t('auth.otpRecaptchaFailed');
        }
        switch (code) {
          case 'auth/invalid-phone-number':
            return t('auth.otpBadNumber');
          case 'auth/missing-phone-number':
            return t('auth.phoneRequired');
          case 'auth/quota-exceeded':
          case 'auth/too-many-requests':
            return t('auth.otpQuotaExceeded');
          case 'auth/operation-not-allowed':
            return t('auth.otpProviderDisabled');
          case 'auth/captcha-check-failed':
          case 'auth/invalid-app-credential':
            return t('auth.otpRecaptchaFailed');
          case 'auth/unauthorized-domain':
            return t('auth.otpUnauthorizedDomain');
          case 'auth/network-request-failed':
            return t('auth.otpNetwork');
          case 'auth/internal-error':
            return t('auth.otpInternal');
          default:
            return `${t('auth.otpSendFailed')} (${code || 'unknown'})`;
        }
      })();
      setError(mapped);
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!otpCode) { setError(t('auth.otpRequired')); return; }
    setLoading(true);
    try {
      const cred = await confirmPhoneSignIn(confirmation, otpCode);
      const user = cred?.user;
      if (!user) {
        setError(t('auth.otpVerifyFailed'));
        return;
      }

      // Phone signup / auto-link flow: first-time phone users get a
      // parent profile auto-created. If their number is already on a
      // student's `fatherPhoneNumber` we bind them to that tenant +
      // children immediately; otherwise they land on /link-account to
      // wait for the madrasa admin to add them.
      let profile = null;
      try { profile = await userService.getUserProfile(user.uid); } catch (_) {}

      if (!profile) {
        const match = await studentService.findByFatherPhone(user.phoneNumber);
        await userService.upsertUserProfile(user.uid, {
          role: ROLES.PARENT,
          phone: user.phoneNumber || null,
          tenantId: match.tenantId || null,
          linkedStudentIds: match.studentIds || [],
          createdViaPhone: true,
        });
        profile = {
          role: ROLES.PARENT,
          tenantId: match.tenantId || null,
          linkedStudentIds: match.studentIds || [],
        };
      } else if (
        isParentRole(profile.role) &&
        !profile.tenantId &&
        user.phoneNumber
      ) {
        // Returning phone user who was waiting on admin linking — retry
        // the auto-link every time they log in.
        const match = await studentService.findByFatherPhone(user.phoneNumber);
        if (match.tenantId && match.studentIds.length > 0) {
          await userService.upsertUserProfile(user.uid, {
            tenantId: match.tenantId,
            linkedStudentIds: match.studentIds,
          });
          profile = { ...profile, tenantId: match.tenantId, linkedStudentIds: match.studentIds };
        }
      }

      try { if (typeof refreshProfile === 'function') await refreshProfile(); } catch (_) {}

      // Route based on whether we successfully linked to a tenant.
      if (isParentRole(profile?.role) && profile?.tenantId) {
        navigate('/parent/dashboard');
      } else if (isParentRole(profile?.role)) {
        navigate('/link-account');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('[phone-verify]', err);
      setError(t('auth.otpVerifyFailed'));
    } finally { setLoading(false); }
  };

  return (
    <Box sx={(th) => ({
      position: 'relative', minHeight: '100dvh',
      display: 'flex', flexDirection: { xs: 'column', md: 'row' },
      bgcolor: th.palette.sacred.surface,
    })}>
      <ZelligeBackdrop />

      {/* Hero arch panel — fluid */}
      <Box sx={(th) => ({
        position: 'relative',
        flex: { md: '1 1 48%' },
        minHeight: { xs: 'clamp(200px, 30dvh, 280px)', md: '100dvh' },
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: th.palette.sacred.primary,
        color: th.palette.sacred.onPrimary,
        overflow: 'hidden',
        p: 'clamp(1rem, 4vw, 4rem)',
      })}>
        <Box
          aria-hidden
          className="medersa-pattern"
          sx={{
            position: 'absolute', inset: 0,
            '--motif-opacity': 0.25,
            '--motif-size': 'clamp(180px, 22vw, 340px)',
            '--motif-color': '#ffffff',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }} aria-hidden="true">
          <MaghribiHeading variant="display" glow tone="onPrimary" component="div" sx={{ fontSize: { xs: 'clamp(3rem, 14vw, 5rem)', md: 'clamp(5rem, 9vw, 8rem)' }, color: 'inherit', mb: 2 }}>
            البيان
          </MaghribiHeading>
          <Box sx={{ width: 'clamp(40px, 6vw, 80px)', height: '1px', bgcolor: 'sacred.secondary', opacity: 0.7, mx: 'auto', mb: { xs: 2, md: 3 } }} />
          <Typography sx={{ fontFamily: 'var(--sacred-font-display)', fontSize: 'clamp(1rem, 2.2vw, 1.55rem)', opacity: 0.95 }}>
            نُورُ الحفظ، بيانُ الفهم
          </Typography>
        </Box>

        {/* Brand motif decorations — Arabic zellige tile, fluid */}
        <ArabicMotif color="#fff" sx={{ position: 'absolute', top: '-5%', right: '-5%', width: 'clamp(180px, 28vw, 380px)', aspectRatio: '397 / 229', opacity: 0.08 }} />
        <ArabicMotif color="#fff" sx={{ position: 'absolute', bottom: '-8%', left: '-8%', width: 'clamp(220px, 34vw, 460px)', aspectRatio: '397 / 229', opacity: 0.05 }} />
      </Box>

      {/* Form panel — fluid */}
      <Box sx={{
        flex: { md: '1 1 52%' },
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 'clamp(0.75rem, 2.5vh, 3rem) clamp(1.5rem, 4vw, 4rem)',
        minHeight: { xs: 'auto', md: '100dvh' },
        maxHeight: { md: '100dvh' },
        overflowY: 'auto',
      }}>
        <Box sx={{ width: '100%', maxWidth: 'min(460px, 92vw)' }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{ mb: 'clamp(0.25rem, 1vh, 1rem)', color: 'sacred.onSurfaceVariant' }}
            aria-label={t('common.back')}
          >
            <ArrowForward sx={{ transform: 'scaleX(-1)' }} />
          </IconButton>

          <MaghribiHeading variant="headline" component="h1" sx={{ fontSize: 'clamp(1.5rem, 4vw, 2.1rem)', lineHeight: 1.4, mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}>
            {t('auth.loginHeading')}
          </MaghribiHeading>
          <Typography sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurfaceVariant', lineHeight: 1.6, mb: 'clamp(1rem, 2.5vh, 2rem)', fontSize: 'clamp(0.9rem, 1.8vw, 1rem)' }}>
            {t('auth.loginCaption')}
          </Typography>

          <Button
            fullWidth
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={(th) => ({
              mb: 'clamp(0.75rem, 2vh, 1.5rem)', py: 'clamp(0.5rem, 1.2vh, 1.1rem)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
              bgcolor: th.palette.sacred.surfaceContainerLowest,
              color: th.palette.sacred.onSurface,
              border: `1px solid ${th.palette.sacred.outlineVariant}`,
              fontFamily: 'var(--sacred-font-body)',
              fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
              '&:hover': {
                bgcolor: th.palette.sacred.surfaceContainerLow,
                borderColor: th.palette.sacred.primary,
              },
            })}
          >
            <Box component="img" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" sx={{ width: 20, height: 20, flexShrink: 0 }} />
            <Box component="span">{t('auth.googleCta')}</Box>
          </Button>

          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(''); }}
            variant="fullWidth"
            sx={{
              mb: 'clamp(0.75rem, 2vh, 1.5rem)', minHeight: 40,
              '& .MuiTabs-indicator': { backgroundColor: 'sacred.secondary', height: 3 },
              '& .MuiTab-root': {
                fontFamily: 'var(--sacred-font-display)', fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                textTransform: 'none', color: 'sacred.onSurfaceVariant',
                minHeight: 40, py: 1,
                '&.Mui-selected': { color: 'sacred.primary' },
              },
            }}
          >
            <Tab label={t('auth.email')} />
            <Tab label={t('auth.phone')} />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)', borderRadius: 2 }}>{error}</Alert>}

          {tab === 0 && (
            <form onSubmit={handleEmailSubmit}>
              <TextField size="small" label={t('auth.email')} type="email" variant="outlined" fullWidth required sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }} value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField size="small" label={t('auth.password')} type="password" variant="outlined" fullWidth required sx={{ mb: 'clamp(0.75rem, 2vh, 1.5rem)' }} value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button
                type="submit" fullWidth variant="contained" color="primary"
                disabled={loading}
                sx={{ py: 'clamp(0.5rem, 1.2vh, 1rem)', fontSize: 'clamp(0.95rem, 2vw, 1.05rem)', fontFamily: 'var(--sacred-font-display)' }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.signIn')}
              </Button>
            </form>
          )}

          {tab === 1 && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)', fontFamily: 'var(--sacred-font-body)' }}
              >
                {t('auth.phoneHelp')}
              </Typography>
              <TextField size="small" label={t('auth.phone')} type="tel" variant="outlined" fullWidth sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }} placeholder="+212..." value={phone} onChange={(e) => setPhone(e.target.value)} disabled={Boolean(confirmation)} />
              {!confirmation ? (
                <Button fullWidth variant="contained" color="primary" onClick={handleSendOtp} disabled={loading} sx={{ py: 'clamp(0.5rem, 1.2vh, 1rem)', fontFamily: 'var(--sacred-font-display)' }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.sendOtp')}
                </Button>
              ) : (
                <>
                  <TextField size="small" label={t('auth.otpCode')} variant="outlined" fullWidth sx={{ mb: 'clamp(0.75rem, 2vh, 1.5rem)' }} value={otpCode} onChange={(e) => setOtpCode(e.target.value)} />
                  <Button fullWidth variant="contained" color="primary" onClick={handleVerifyOtp} disabled={loading} sx={{ py: 'clamp(0.5rem, 1.2vh, 1rem)', fontFamily: 'var(--sacred-font-display)' }}>
                    {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.verifyOtp')}
                  </Button>
                </>
              )}
              <div id="recaptcha-container" />
            </Box>
          )}

          {/* Demo quick-login accordion */}
          <Box sx={{ mt: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
            <Button
              fullWidth
              onClick={() => setDemoOpen((v) => !v)}
              startIcon={<Science />}
              endIcon={<ExpandMore sx={{ transform: demoOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 220ms' }} />}
              sx={{
                py: 'clamp(0.4rem, 1vh, 0.9rem)', justifyContent: 'space-between',
                bgcolor: (th) => `${th.palette.sacred.secondary}14`,
                color: 'sacred.secondary',
                fontFamily: 'var(--sacred-font-display)',
                borderRadius: 2,
                '&:hover': { bgcolor: (th) => `${th.palette.sacred.secondary}24` },
              }}
            >
              دخول تجريبي — مؤسسة المرواني
            </Button>
            <Collapse in={demoOpen}>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {DEMO_ACCOUNTS.map((acct) => (
                  <Box
                    key={acct.email}
                    onClick={() => loginAsDemo(acct)}
                    sx={(th) => ({
                      p: 1.75, borderRadius: 2,
                      bgcolor: th.palette.sacred.surfaceContainerLow,
                      borderInlineStart: `3px solid ${th.palette.sacred.primary}`,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      transition: 'all 220ms ease',
                      '&:hover': { bgcolor: th.palette.sacred.surfaceContainer, transform: 'translateX(-3px)' },
                    })}
                  >
                    <Chip
                      size="small"
                      label={acct.label}
                      sx={{ fontFamily: 'var(--sacred-font-label)', fontSize: '0.65rem', bgcolor: 'sacred.primary', color: 'sacred.onPrimary', flexShrink: 0 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'var(--sacred-font-display)', fontSize: '0.95rem', fontWeight: 600, color: 'sacred.primary' }}>
                        {acct.displayName}
                      </Typography>
                      {acct.description && (
                        <Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.78rem', color: 'sacred.onSurfaceVariant' }}>
                          {acct.description}
                        </Typography>
                      )}
                      <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'sacred.outlineVariant' }}>
                        {acct.email}
                      </Typography>
                    </Box>
                    <ArrowForward sx={{ transform: 'scaleX(-1)', color: 'sacred.primary', fontSize: 18 }} />
                  </Box>
                ))}
                <Typography sx={{ fontFamily: 'var(--sacred-font-body)', fontSize: '0.75rem', color: 'sacred.onSurfaceVariant', textAlign: 'center', mt: 1 }}>
                  بيانات تجريبية — كلمة مرور المعلمين: Teacher123! · أولياء الأمور: Parent123!
                </Typography>
              </Stack>
            </Collapse>
          </Box>

          <Divider sx={{ my: 'clamp(0.75rem, 2vh, 2rem)', opacity: 0.4 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'sacred.onSurfaceVariant', mb: 0.5 }}>
              {t('auth.noAccount')}{' '}
              <Link component={RouterLink} to="/register" sx={{ color: 'sacred.primary', fontWeight: 600 }} underline="hover">
                {t('auth.createAccount')}
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ color: 'sacred.onSurfaceVariant' }}>
              {t('auth.newMadrasa')}{' '}
              <Link component={RouterLink} to="/signup" sx={{ color: 'sacred.primary', fontWeight: 600 }} underline="hover">
                {t('auth.signupMadrasa')}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
